import { v4 as uuidv4 } from 'uuid'
import { camelize } from 'inflected'
import { saveRecord, deleteRecord } from './records'
import { Storage } from 'aws-amplify'

const upload = async (file, prefix, progressCallback) => {
  const extension = file.name.split('.').pop()
  const filename = `${prefix}/${uuidv4()}.${extension}`
  const { key } = await Storage.put(filename, file, {
    level: 'protected',
    contentType: file.type,
    progressCallback (progress) {
      const percent = Math.round((progress.loaded / progress.total) * 100)
      if (typeof progressCallback === 'function') progressCallback(percent)
      console.log(`Uploaded: ${percent} = ${progress.loaded}/${progress.total}`)
    }
  })
  return key
}

export class Editable {
  constructor(type, attributes={}, associations={}) {
    this.type = type
    this.attributes = { ...attributes }
    this.associations = {}
    this.__delete = false
    Object.entries(associations).forEach(([ type, records ]) => {
      this.associations[type] = records.map(record => {
        if (record instanceof Editable) {
          return record
        } else {
          return new Editable(type, record)
        }
      })
    })
  }
  markForDelete () {
    this.__delete = true
  }
  async save () {
    const record = await saveRecord(this.type, this.attributes)
    const parentID = this.attributes.id = record.id
    // loop through all associated records calling save / destroy and collect promises
    await Promise.all(Object.values(this.associations).map(records => {
      const idKey = `${camelize(this.type, false)}ID`
      return records.map(async (record) => {
        record.attributes[idKey] = parentID
        return record[record.__delete ? 'destroy' : 'save'].call(record)
      })
    }).flat())
    return record
  }
  async destroy () {
    if (!this.attributes.id) {
      return Promise.resolve()
    }
    await deleteRecord({
      type: this.type,
      id: this.attributes.id
    })
  }
}

export class EditablePost extends Editable {
  constructor(attributes, associations={}) {
    super('Post', {
      title: '',
      teaser: '',
      postedAt: new Date().toJSON(),
      lat: null,
      lng: null,
      ...attributes
    }, {
      Content: [],
      ...associations
    })
  }
  get postedAt () {
    return new Date(this.attributes.postedAt)
  }
  set postedAt (value) {
    this.attributes.postedAt = value.toJSON()
  }
  get contents () {
    return this.associations['Content'].filter(i => !i.__delete)
  }
  addContent (index) {
    const content = new EditableContent({
      postID: this.attributes.id,
      body: '',
      location: '',
      lat: null,
      lng: null,
      media: []
    })
    this.associations['Content'].splice(index + 1, 0, content)
  }
  removeContent (content) {
    content.__delete = true
  }
}

export class EditableContent extends Editable {
  constructor(attributes, associations={}) {
    super('Content', attributes, {
      Media: [],
      ...associations
    })
  }
  get media () {
    return this.associations['Media'].filter(i => (i instanceof Editable) && !i.__delete)
  }
  set media (value) {
    value.map(item => {
      if (!(item instanceof Editable)) {
        item = new EditableMedia({
          contentID: this.attributes.id
        }, item)
        this.associations['Media'].push(item)
      }
    })
  }
  addMedia (index) {
    const content = new EditableMedia()
    this.associations['Media'].splice(index + 1, 0, content)
  }
}

export class EditableMedia extends Editable {
  constructor(attributes={}, file) {
    super('Media', {
      caption: '',
      lat: null,
      lng: null,
      ...attributes
    }, {})
    if (file) {
      this.file = file
      this.attributes.contentType = file.type
    }
  }
  async save () {
    // upload a file if one is present
    if (this.file) {
      const key = await upload(this.file, this.prefix, this.onProgress)
      this.attributes.key = `${this.prefix}/${key}`
    }
    const record = await saveRecord(this.type, this.attributes)
    return record
  }
}
